import { motion, type Variants } from "motion/react";
import type React from "react";

/**
 * Card — primitive Apple-like usado em todo o app.
 *
 * Borda fininha cinza (--wf-ink-5), shadow sutil, radius 12px.
 * Substitui o uso direto da classe `wf-box` (que tem borda preta dura
 * --wf-line e era a estética de "papel impresso" antiga).
 *
 * Quando precisar de hover-lift, passe `hover`.
 * Quando precisar animar entrada (delay encadeado), passe `delay`.
 */

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  hover?: boolean;
  delay?: number;
  padding?: number | string;
  /** Tom do card. Default = neutro paper */
  tone?: "default" | "success" | "warning" | "danger" | "muted";
}

const TONES = {
  default: { bg: "var(--wf-paper)", border: "var(--wf-ink-5)" },
  success: { bg: "rgba(92,184,92,0.05)", border: "rgba(92,184,92,0.3)" },
  warning: { bg: "#fff8ef", border: "var(--nd-orange)" },
  danger: { bg: "#fff0ee", border: "#ff5577" },
  muted: { bg: "#fafaf6", border: "var(--wf-ink-5)" },
} as const;

const fadeInVariants: Variants = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
};

export function Card({
  children,
  className = "",
  style,
  hover = false,
  delay,
  padding = 18,
  tone = "default",
  ...rest
}: CardProps) {
  const t = TONES[tone];
  const cardClass = `wf-card${hover ? " wf-card-hover" : ""}${className ? ` ${className}` : ""}`;

  if (typeof delay === "number") {
    return (
      <motion.div
        {...(rest as React.ComponentProps<typeof motion.div>)}
        className={cardClass}
        style={{
          background: t.bg,
          borderColor: t.border,
          padding,
          ...style,
        }}
        variants={fadeInVariants}
        initial="initial"
        animate="animate"
        transition={{ duration: 0.4, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    );
  }

  return (
    <div
      {...rest}
      className={cardClass}
      style={{
        background: t.bg,
        borderColor: t.border,
        padding,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Label maiusculado em cima do card. Aceita ícone opcional à esquerda. */
export function CardLabel({
  icon,
  children,
  style,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        fontSize: 11,
        fontWeight: 600,
        color: "var(--wf-ink-2)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: 12,
        ...style,
      }}
    >
      {icon}
      <span>{children}</span>
    </div>
  );
}

/** KPI card — número grande + label + sub. Usa accent pra cor do número. */
export function KpiCard({
  label,
  value,
  subtitle,
  hint,
  accent,
  icon,
  delay,
  hover = false,
  onClick,
  href,
}: {
  label: string;
  value: string;
  subtitle?: React.ReactNode;
  hint?: React.ReactNode;
  accent?: string;
  icon?: React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
  delay?: number;
  hover?: boolean;
  onClick?: () => void;
  href?: string;
}) {
  const IconCmp = icon;
  const inner = (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        {IconCmp && (
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              background: `${accent ?? "var(--nd-orange)"}1a`,
              color: accent ?? "var(--nd-orange)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IconCmp size={12} strokeWidth={2} />
          </span>
        )}
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--wf-ink-3)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </span>
      </div>
      <div
        className="wf-display"
        style={{
          fontSize: 28,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          color: accent ?? "var(--wf-ink)",
          lineHeight: 1.05,
        }}
      >
        {value}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: "var(--wf-ink-2)", marginTop: 6 }}>
          {subtitle}
        </div>
      )}
      {hint && (
        <div style={{ fontSize: 11, color: "var(--wf-ink-3)", marginTop: 6, lineHeight: 1.5 }}>
          {hint}
        </div>
      )}
    </>
  );

  if (href) {
    // Link wrap, mas o Card primitive lida com style/borda
    return (
      <a
        href={href}
        style={{ textDecoration: "none", color: "inherit", display: "block" }}
      >
        <Card hover={hover || true} delay={delay}>
          {inner}
        </Card>
      </a>
    );
  }
  return (
    <Card hover={hover} delay={delay} onClick={onClick} style={{ cursor: onClick ? "pointer" : undefined }}>
      {inner}
    </Card>
  );
}
